// Blocos das páginas internas do site público (preços, agências, como funciona), no sistema visual da home (home.css).
import { useState, type FormEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Check, type LucideIcon } from 'lucide-react'
import { TRIAL_DAYS } from '@impulso/shared'
import type { PlanDto } from '@impulso/shared/api/billing'
import { api, errorMessage } from '../../lib/api'
import { planFeatures, priceLabel } from '../billing/api'
import { trackGlow } from './motion'
import './site.css'
import './home.css'

/** Seção das páginas internas: mesmo ritmo e container da home (.sh-sec / .sh-wrap). Use dentro de <div className="sh">. */
export function Section({ children, alt, id, hero }: { children: ReactNode; alt?: boolean; id?: string; hero?: boolean }) {
  return (
    <section id={id} className={hero ? 'sh-hero' : `sh-sec ${alt ? 'sh-sec-alt' : ''}`}>
      {hero && <div className="sh-glow" aria-hidden />}
      <div className="sh-wrap">{children}</div>
    </section>
  )
}

export function Faq({ items }: { items: Array<[string, ReactNode]> }) {
  return (
    <div className="site-faq sh-faq">
      {items.map(([q, a]) => <details key={q}><summary>{q}</summary><p>{a}</p></details>)}
    </div>
  )
}

export function FinalCta({ title = 'Comece hoje, sem cartão', sub, agency }: { title?: string; sub?: string; agency?: boolean }) {
  return (
    <section className="sh-sec-cta">
      <div className="sh-wrap">
        <div className="sh-cta">
          <div>
            <h2>{title}</h2>
            <p>{sub ?? `${TRIAL_DAYS} dias grátis com diagnóstico completo das suas contas. Cancele quando quiser.`}</p>
          </div>
          <Link to={agency ? '/cadastro?tipo=agencia' : '/cadastro'} className="sh-btn sh-btn-light">Testar grátis <ArrowRight size={16} /></Link>
        </div>
      </div>
    </section>
  )
}

export function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <div className="sh-bullets">
      {items.map((t, i) => <div key={i}><Check size={16} strokeWidth={3} aria-hidden /><span>{t}</span></div>)}
    </div>
  )
}

/** Cartão de destaque da home (ícone, título, texto), com o brilho que segue o mouse. */
export function FeatureCard({ icon: Icon, title, children }: { icon?: LucideIcon; title: string; children: ReactNode }) {
  return (
    <div className="sh-card">
      <div className="sh-card-in" onPointerMove={trackGlow}>
        {Icon && <Icon size={20} className="sh-card-ic" aria-hidden />}
        <h3>{title}</h3>
        <p>{children}</p>
      </div>
    </div>
  )
}

export function PlanCard({ plan, highlight }: { plan: PlanDto; highlight?: boolean }) {
  const extra = plan.kind === 'agency' && plan.extraCompanyPrice != null && plan.price > 0
    ? `+ ${priceLabel({ ...plan, price: plan.extraCompanyPrice })} por empresa além do limite`
    : null
  return (
    <div className={`sh-plan ${highlight ? 'is-hl' : ''}`}>
      <div className="sh-plan-head"><h3>{plan.name}</h3>{highlight && <span className="sh-pill">Mais escolhido</span>}</div>
      <div className="sh-plan-price"><b>{priceLabel(plan)}</b>{plan.price > 0 && <span>/mês</span>}</div>
      {plan.kind === 'agency' && <div className="sh-plan-extra">{extra}</div>}
      <Bullets items={planFeatures(plan)} />
      <div className="sh-plan-cta">
        {plan.price > 0
          ? <Link to={plan.kind === 'agency' ? '/cadastro?tipo=agencia' : '/cadastro'} className={`sh-btn sh-btn-block ${highlight ? 'sh-btn-cta' : 'sh-btn-ghost'}`}>Testar {TRIAL_DAYS} dias grátis</Link>
          : <a href="#contato" className="sh-btn sh-btn-block sh-btn-ghost">Falar com a gente</a>}
      </div>
    </div>
  )
}

export function LeadForm({ kind }: { kind: 'business' | 'agency' | 'other' }) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [message, setMessage] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'ok'>('idle')
  const [error, setError] = useState<string | null>(null)
  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setState('busy')
    try {
      await api.post('/public/leads', { email, name: name || undefined, kind, message: message || undefined })
      setState('ok')
    } catch (err) {
      setError(errorMessage(err))
      setState('idle')
    }
  }
  if (state === 'ok') return <div className="form-ok">Recebemos seu contato. Respondemos em até 1 dia útil.</div>
  return (
    <form className="stack" style={{ gap: 12 }} onSubmit={submit}>
      {error && <div className="form-error" role="alert">{error}</div>}
      <div className="grid g-2" style={{ gap: 12 }}>
        <div className="field"><label htmlFor="lead-name">Nome</label><input id="lead-name" className="input" value={name} onChange={(e) => setName(e.target.value)} /></div>
        <div className="field"><label htmlFor="lead-email">E-mail</label><input id="lead-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
      </div>
      <div className="field"><label htmlFor="lead-msg">Como podemos ajudar?</label><textarea id="lead-msg" className="textarea" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Quantos clientes você atende, quanto investem por mês…" /></div>
      <button type="submit" className="sh-btn sh-btn-cta" style={{ alignSelf: 'flex-start' }} disabled={state === 'busy'}>{state === 'busy' ? 'Enviando…' : 'Enviar'}</button>
      <p className="small muted">Usamos seu e-mail só para responder. Veja a <Link to="/privacidade" className="strong">política de privacidade</Link>.</p>
    </form>
  )
}
